import { useEffect } from "react";
import { Phone, Mail, Clock, TrendingUp, Shield, Zap, CheckCircle, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";
import { Link } from "react-router-dom";

export default function EstateAgentsPage() {
  
  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate class
    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="For Estate Agents - Accelerate Your Property Transactions"
        description="Upfront search packs for estate agents. Reduce fall-throughs by 60+ days with fast property searches. Get results in 5 working days guaranteed."
        keywords="estate agent property searches, upfront search packs, fast property transactions, reduce fall-throughs"
        canonicalUrl="/estate-agents"
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "Estate Agents" }])}
      />
      <Navbar />

      {/* Hero Section with Video Backdrop */}
      <section className="relative py-32 bg-wl-navy overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/estate-agent-for-sale.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>
        
        {/* Single overlay for darkening */}
        <div className="absolute inset-0 bg-wl-navy/80"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Say Goodbye To Long<br />
              <span className="text-green-400">Transaction Wait Times</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Accelerate your property transactions with upfront information<br />
              to reduce fall-throughs and speed up completion times.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="bg-wl-green hover:bg-wl-green/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Get Free Strategy Session
              </Link>
              <a 
                href="tel:01923620240"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 backdrop-blur-sm flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call 01923 620240/41
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              The Problem With The Current System
            </h2>
            <p className="text-xl text-wl-gray max-w-4xl mx-auto">
              Local authority search delays remain a major hurdle in property transactions, driven by extended council processing times and critical data surfacing too late in the process.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-red-500 scroll-animate">
              <div className="text-4xl font-bold text-red-600 mb-4">170 Days</div>
              <p className="text-red-600">The average time to sell a house in England and Wales is approximately 170 days (5.6 months)</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-red-500 scroll-animate">
              <div className="text-4xl font-bold text-red-600 mb-4">£1 Billion</div>
              <p className="text-red-600">Cost of fall-through sales to sellers and buyers in 2025 (£400 million in fall-throughs and 4 million working days lost equating to £600 million)</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-red-500 scroll-animate">
              <div className="text-4xl font-bold text-red-600 mb-4">40 Days</div>
              <p className="text-red-600">Number of days lost in search delays in most London Boroughs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              Our Solution: Upfront Search Packs
            </h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              We tackle this issue head on.  Get search results back within 5 working days - guaranteed.  Faster than going direct to councils.
              Speed up completions by more than 60 days and reduce fall-throughs by identifying issues early, before they derail the transaction.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate">
              <h3 className="text-2xl font-bold text-wl-navy mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-wl-blue rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-semibold text-wl-navy mb-2">Order Upfront Search Pack</h4>
                    <p className="text-wl-gray">Commission searches before exchange of contracts using our streamlined ordering system.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-wl-green rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-semibold text-wl-navy mb-2">Fast-Track Processing</h4>
                    <p className="text-wl-gray">We use our direct council connections and 20+ years of relationships to expedite your searches.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-wl-navy rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-semibold text-wl-navy mb-2">Accelerated Completion</h4>
                    <p className="text-wl-gray">Complete transactions 60+ days faster, reduce fall-throughs, and get paid sooner.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-wl-light p-8 rounded-2xl scroll-animate">
              <h3 className="text-2xl font-bold text-wl-navy mb-6">Average Turnaround Times</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium">London Borough of Hackney</span>
                  <span className="text-red-600 font-bold">40 Days</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium">Thurrock</span>
                  <span className="text-red-600 font-bold">25 Days</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium">Camden</span>
                  <span className="text-red-600 font-bold">40 Days</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="font-medium">Charnwood</span>
                  <span className="text-red-600 font-bold">25 Days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              How You Benefit As An Estate Agent
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-wl-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-wl-blue" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Rapid Results</h3>
              <p className="text-wl-gray text-sm">Slash transaction time by 60 days with upfront search packs</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-wl-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-wl-green" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Competitive Edge</h3>
              <p className="text-wl-gray text-sm">Outpace competitors with faster transactions</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-wl-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-wl-navy" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Enhanced Assurance</h3>
              <p className="text-wl-gray text-sm">Reduced risk of fall-throughs</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-16 h-16 bg-wl-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-wl-green" />
              </div>
              <h3 className="text-lg font-bold text-wl-navy mb-3">Increased Revenue</h3>
              <p className="text-wl-gray text-sm">Free from solicitor chasing, unlock sales potential</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-wl-navy">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center scroll-animate">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">
            Our Mission: Your Success
          </h2>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-10 border border-white/20">
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-4xl mx-auto">
              "At Whitefield Legal Services, we believe every estate agent deserves a dedicated and experienced search partner. 
              Our mission is to <span className="text-green-400 font-semibold">accelerate property transactions</span> by delivering{' '}
              <span className="text-green-400 font-semibold">fast turnaround times</span>, accurate and high-quality reports, and{' '}
              <span className="text-green-400 font-semibold">clear, proactive communication</span>—ensuring our clients meet their 
              deadlines with confidence."
            </p>
            <div className="text-white/80 text-lg md:text-xl font-medium">
              This means faster commissions, happier clients, and more successful transactions for your business.
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              Why Choose Whitefield Legal?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-wl-light p-8 rounded-xl scroll-animate">
              <Clock className="w-12 h-12 text-wl-blue mb-4" />
              <h3 className="text-xl font-bold text-wl-navy mb-4">Guaranteed Speed</h3>
              <ul className="space-y-2 text-wl-gray">
                <li>• 7-10 working day guarantee</li>
                <li>• Direct council connections</li>
                <li>• Priority processing available</li>
                <li>• Real-time status updates</li>
              </ul>
            </div>
            
            <div className="bg-wl-light p-8 rounded-xl scroll-animate">
              <Shield className="w-12 h-12 text-wl-green mb-4" />
              <h3 className="text-xl font-bold text-wl-navy mb-4">Fully Compliant</h3>
              <ul className="space-y-2 text-wl-gray">
                <li>• £2M professional indemnity insurance</li>
                <li>• 99.9% lender acceptance rate</li>
                <li>• Property Ombudsman affiliated</li>
                <li>• Searchcode registered</li>
              </ul>
            </div>
            
            <div className="bg-wl-light p-8 rounded-xl scroll-animate">
              <TrendingUp className="w-12 h-12 text-wl-navy mb-4" />
              <h3 className="text-xl font-bold text-wl-navy mb-4">Proven Results</h3>
              <ul className="space-y-2 text-wl-gray">
                <li>• 20+ years experience</li>
                <li>• National coverage</li>
                <li>• Commission opportunities</li>
                <li>• Personal account management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
              What Our Clients Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-blue scroll-animate">
              <div className="mb-6">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-wl-gray italic">
                  "Without their prompt services, I would never have been in this field for more than a decade. 
                  They have made my work stress-free and it's such a delight to know there are reliable professionals."
                </p>
              </div>
              <div className="font-semibold text-wl-navy">Vijaya Ratnam</div>
              <div className="text-wl-gray text-sm">Consultant Solicitor, Starck Uberoi</div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-green scroll-animate">
              <div className="mb-6">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-wl-gray italic">
                  "None have the quality of personal service and ease of use as Whitefield. 
                  So responsive, proactive and readily willing to assist with any queries."
                </p>
              </div>
              <div className="font-semibold text-wl-navy">Hitesh Patel</div>
              <div className="text-wl-gray text-sm">Harrow Law Practice</div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-navy scroll-animate">
              <div className="mb-6">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-wl-gray italic">
                  "We can pick up the phone and speak to someone straight away. 
                  Exceptional customer service and very user-friendly platform."
                </p>
              </div>
              <div className="font-semibold text-wl-navy">Nadia Karaivanova</div>
              <div className="text-wl-gray text-sm">CVS Law</div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-wl-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Get Faster Transaction Times Starting Today
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            Ready to experience smoother, faster, less-stressful transactions for you and your clients? 
            Let's have a free strategy session to show you how it works.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center bg-wl-green hover:bg-wl-green/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              <Mail className="w-5 h-5 mr-2" />
              Get Free Strategy Session
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <a 
              href="tel:01923620240" 
              className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors border border-white/20"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call 01923 620240/41
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 